import Keycloak, { type KeycloakConfig } from "keycloak-js";
import { setAuthenticated, setLoggedOut, setLoading } from "../store/authSlice";

const TOKEN_REFRESH_DELTA = 70;
const TOKEN_REFRESH_INTERVAL = 60000;

export default class KeycloakManager {
  private static keycloakInstance: Keycloak;
  private static refreshTokenInterval?: number;

  static async initKeycloak(dispatch): Promise<void> {
    dispatch(setLoading(true));

    try {
      const response = await fetch("/api/v1/authconfig");
      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }
      const config: KeycloakConfig = await response.json();

      this.keycloakInstance = new Keycloak(config);

      const authenticated = await this.keycloakInstance.init({
        onLoad: "login-required",
        pkceMethod: "S256",
        checkLoginIframe: false,
        scope: "openid profile email",
      });

      // 3. Si está autenticado, guardar el token y usuario en Redux
      if (authenticated) {
        dispatch(
          setAuthenticated({
            token: this.keycloakInstance.token!,
            username: this.getUsername() || "",
          })
        );

        // 4. Configurar el intervalo de refresco de token
        this.refreshTokenInterval = window.setInterval(async () => {
          try {
            const refreshed = await this.keycloakInstance.updateToken(
              TOKEN_REFRESH_DELTA
            );
            if (refreshed) {
              dispatch(
                setAuthenticated({
                  token: this.keycloakInstance.token!,
                  username: this.getUsername() || "",
                })
              );
            }
          } catch (error) {
            console.error("Failed to refresh token", error);
            this.doLogin();
          }
        }, TOKEN_REFRESH_INTERVAL);
      } else {
        // Si no está autenticado, redirigir al login
        await this.keycloakInstance.login();
      }
    } catch (error) {
      console.error("Keycloak initialization error:", error);
      dispatch(setLoggedOut());
    } finally {
      dispatch(setLoading(false));
    }
  }

  static doLogin(): Promise<void> {
    return this.keycloakInstance.login();
  }

  static doLogout(dispatch): Promise<void> {
    if (this.refreshTokenInterval) {
      clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = undefined;
    }
    dispatch(setLoggedOut());
    return this.keycloakInstance.logout();
  }

  static getToken(): string | undefined {
    return this.keycloakInstance.token;
  }

  static getTokenParsed(): Keycloak.KeycloakTokenParsed | undefined {
    return this.keycloakInstance.tokenParsed;
  }

  static getTokenOperators() {
    const parsedToken = this.keycloakInstance.tokenParsed;
    const groups: string[] = parsedToken.groups;
    const orgs = groups
      ?.filter((group) => group.startsWith("/organization_"))
      .map((group) => group.replace("/organization_", ""));
    if (orgs.length === 0) {
      orgs.push(parsedToken.preferred_username);
    }
    return orgs;
  }

  static updateToken(successCallback) {
    return this.keycloakInstance
      .updateToken(5)
      .then(successCallback)
      .catch(this.doLogin);
  }

  static getUsername(): string | undefined {
    const parsedToken = this.keycloakInstance.tokenParsed as
      | Record<string, string>
      | undefined;
    if (!parsedToken) return undefined;
    return parsedToken.name || parsedToken.preferred_username;
  }

  static getUserlogin() {
    const objKeycloak = this.keycloakInstance.tokenParsed;
    return objKeycloak?.preferred_username;
  }
}
